import { useMemo } from 'react';
import { CustomAttributeSidePanelRow } from 'src/components/CustomAttributes/side-panel-row';
import { CLIENT_SECTION_PROPERTIES } from 'src/components/SidePanelTabs/SiteClient/client-section-registry';
import { SITE_SECTION_PROPERTIES } from 'src/components/SidePanelTabs/SiteClient/site-section-registry';
import {
  buildCustomAttributeSidePanelDescriptors,
  useCustomAttributesSidePanel,
} from 'src/graphql/entities/custom-attributes/side-panel-context';
import type {
  SidePanelSectionData,
  SidePanelSurfaceConfig,
} from 'src/graphql/entities/side-panel-personalization/model/descriptors';
import { UserSettingId } from 'src/graphql/entities/user-setting-value/user-setting-value.types';
import { CONVERSATION_BASE_PROPERTIES } from './conversation-section-registry';

function useConversationSections(): SidePanelSectionData[] {
  const customAttributes = useCustomAttributesSidePanel();

  return useMemo(
    () => [
      {
        key: 'conversation' as const,
        label: 'Conversation',
        loading: customAttributes.loading,
        notice: customAttributes.payloadError ?? undefined,
        hidePanelLabel: true,
        properties: [
          ...CONVERSATION_BASE_PROPERTIES,
          ...buildCustomAttributeSidePanelDescriptors(
            customAttributes.rowsByPropertyKey,
            CustomAttributeSidePanelRow,
          ),
        ],
      },
      // card-rendered sections: fields show on the site/client cards, not as
      // panel rows. The site card's form keeps its own field order (no Row
      // descriptors ⇒ the modal disables reordering); ClientCards render
      // their fields in layout order, so the client section opts in.
      {
        key: 'site' as const,
        label: 'Site',
        loading: false,
        properties: SITE_SECTION_PROPERTIES,
      },
      {
        key: 'client' as const,
        label: 'Client',
        loading: false,
        reorderable: true,
        properties: CLIENT_SECTION_PROPERTIES,
      },
    ],
    [
      customAttributes.loading,
      customAttributes.payloadError,
      customAttributes.rowsByPropertyKey,
    ],
  );
}

export const conversationSidePanelSurface: SidePanelSurfaceConfig = {
  surfaceKey: 'conversation',
  settingIds: {
    profiles: UserSettingId.ConversationSidePanelProfiles,
    personalization: UserSettingId.ConversationSidePanelPersonalization,
    groupProfile: UserSettingId.ConversationSidePanelGroupProfile,
  },
  useSections: useConversationSections,
};
